import { createReduxModule } from 'hooks-for-redux'
import { KnownBluetoothDevice } from 'lib/types'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import {
  getKnownBluetoothDevices,
  addKnownBluetoothDevice,
  removeKnownBluetoothDevice,
} from './api/bluetoothPairing'
import { getRecordsById, getRecordsByOrgId } from './modelUtils'
import * as PopUpNotifications from './PopUpNotifications'

export interface BluetoothPairingState {
  known: KnownBluetoothDevice[]
  knownById: { [id: string]: KnownBluetoothDevice }
  knownByOrgId: { [orgId: string]: KnownBluetoothDevice[] }
  loading: boolean
  error?: any
}

const initialState: BluetoothPairingState = {
  known: [],
  knownById: {},
  knownByOrgId: {},
  loading: false,
}

const rebuildIndexes = (known: KnownBluetoothDevice[]): Pick<BluetoothPairingState, 'known' | 'knownById' | 'knownByOrgId'> => ({
  known,
  knownById: getRecordsById<KnownBluetoothDevice>(known),
  knownByOrgId: getRecordsByOrgId<KnownBluetoothDevice>(known),
})

export const [
  use,
  { setKnownForOrg, addKnown, removeKnown, setLoading, clearLoading, setError },
  store,
] = createReduxModule('bluetoothPairing', initialState, {
  setKnownForOrg: (
    state: BluetoothPairingState,
    payload: { orgId: string; devices: KnownBluetoothDevice[] },
  ): BluetoothPairingState => {
    const { orgId, devices } = payload
    const tagged = (devices || []).map(d => ({ ...d, orgId: d.orgId ?? orgId }))
    const others = state.known.filter(d => d.orgId !== orgId)
    return { ...state, ...rebuildIndexes([...others, ...tagged]) }
  },
  addKnown: (state: BluetoothPairingState, device: KnownBluetoothDevice): BluetoothPairingState => {
    const tagged = { ...device, orgId: device.orgId }
    const filtered = state.known.filter(d => d.id !== tagged.id)
    return { ...state, ...rebuildIndexes([...filtered, tagged]) }
  },
  removeKnown: (
    state: BluetoothPairingState,
    payload: { orgId: string; id: string },
  ): BluetoothPairingState => {
    const next = state.known.filter(d => d.id !== payload.id)
    return { ...state, ...rebuildIndexes(next) }
  },
  setLoading: (state: BluetoothPairingState, loading: boolean) => ({ ...state, loading }),
  clearLoading: (state: BluetoothPairingState) => ({ ...state, loading: false }),
  setError: (state: BluetoothPairingState, error: any) => ({ ...state, error }),
})

const handleApiError = (error: any) => {
  clearLoading()
  const msg = error instanceof Error ? error.message : error?.toString?.() ?? 'unknown error'
  PopUpNotifications.fireError({ content: `Bluetooth pairing: ${msg}` })
  setError(error)
  throw error
}

export const reloadForOrg = (orgId: string | undefined | null): Promise<void> => {
  if (!orgId || orgId === ALL_ORGS_SELECTED) return Promise.resolve()
  setLoading(true)
  return getKnownBluetoothDevices(orgId)
    .then(devices => {
      setKnownForOrg({ orgId, devices })
      clearLoading()
    })
    .catch(handleApiError)
}

export const addToWhitelist = (
  orgId: string,
  payload: { name: string },
): Promise<KnownBluetoothDevice> => {
  setLoading(true)
  return addKnownBluetoothDevice(orgId, payload)
    .then(device => {
      addKnown({ ...device, orgId: device.orgId ?? orgId })
      clearLoading()
      return device
    })
    .catch(handleApiError)
}

export const removeFromWhitelist = (orgId: string, id: string): Promise<void> => {
  setLoading(true)
  return removeKnownBluetoothDevice(orgId, id)
    .then(() => {
      removeKnown({ orgId, id })
      clearLoading()
    })
    .catch(handleApiError)
}
